import React, { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import { useFormik } from "formik";
import TextInput from "../../common/textfield/TextInput";
import SearchSelect from "../../common/textfield/SearchSelect";
import { useNavigate } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import { Alert, Skeleton, Tooltip } from "@mui/material";
import { DURATION_PRESETS, serviceCategorySchema } from "./constants";
import { useSchedulingOrg } from "../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { useCategoryOrganizations } from "../../../hooks/ReactQueryHooks/useCategoryOrganizations";
import { createServiceCategory } from "../../api/Scheduling/serviceCategories";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import CategorySlotExample from "./CategorySlotExample";
import "./Scheduling.css";

const AddCategory = () => {
  const navigate = useNavigate();
  const context = useSchedulingOrg();
  const { organizationRef } = context;
  const owners = useCategoryOrganizations(context);
  const [btnLoading, setBtnLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      organizationRef: organizationRef || "",
      code: "",
      name: "",
      durationMinutes: 30,
      bookingIncrementMinutes: 5,
    },
    validationSchema: serviceCategorySchema,
    onSubmit: (values) => {
      if (owners.isLoading || owners.isError || !owners.options.some(org => org.value === values.organizationRef)) {
        formik.setFieldError("organizationRef", "Select an available organization.");
        return;
      }
      setBtnLoading(true);
      createServiceCategory({
        organizationRef: values.organizationRef,
        code: values.code.trim(),
        name: values.name.trim(),
        durationMinutes: Number(values.durationMinutes),
        bookingIncrementMinutes: Number(values.bookingIncrementMinutes),
      })
        .then(() => {
          success("Schedule category created");
          navigate("/app/manage-schedule-category", { state: { schedulingCreated: true } });
        })
        .catch((error) => failed(schedulingErrorMessage(error)))
        .finally(() => setBtnLoading(false));
    },
  });

  const { setFieldValue } = formik;
  useEffect(() => {
    setFieldValue("organizationRef", organizationRef || "");
  }, [organizationRef, setFieldValue]);

  return (
    <section className="common-listing">
      <div className="heading-wrap mb-3">
        <Tooltip title="Go back">
          <div style={{ marginRight: "1rem", cursor: "pointer" }} onClick={() => navigate(-1)}>
            <ArrowBack />
          </div>
        </Tooltip>
      </div>
      <div className="custom-card p-4">
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          {owners.isError && <Alert severity="error" action={<button type="button" onClick={owners.retry}>Retry</button>}>Could not load organizations.</Alert>}
            <Row className="mt-3">
              <Col>
                {owners.isLoading ? (
                  <Skeleton height={40} />
                ) : (
                  <SearchSelect
                    keyField="organizationRef"
                    label="Organization"
                    aria-label="Organization"
                    formik={formik}
                    placeholder="Select Organization"
                    options={owners.options}
                    value={owners.options.find((org) => org.value === formik.values.organizationRef) || null}
                    isDisabled={owners.isError || btnLoading}
                    isClearable={false}
                    onChange={(selected) => {
                      formik.setFieldValue("organizationRef", selected?.value || "");
                    }}
                    required
                  />
                )}
              </Col>
            </Row>
          <Row>
            <Col>
              <TextInput keyField="code" label="Code" formik={formik} placeholder="CONSULT" />
            </Col>
            <Col>
              <TextInput keyField="name" label="Name" formik={formik} placeholder="Initial Consultation" />
            </Col>
          </Row>
          <div className="scheduling-preset-row">
            {DURATION_PRESETS.map((minutes) => (
              <button
                type="button"
                key={minutes}
                onClick={() => formik.setFieldValue("durationMinutes", minutes)}
              >
                {minutes} min
              </button>
            ))}
          </div>
          <Row>
            <Col>
              <TextInput
                keyField="durationMinutes"
                label="Duration minutes"
                type="number"
                formik={formik}
                placeholder="30"
              />
            </Col>
            <Col>
              <TextInput
                keyField="bookingIncrementMinutes"
                label="Booking increment"
                type="number"
                formik={formik}
                placeholder="5"
              />
            </Col>
          </Row>
          <CategorySlotExample
            durationMinutes={formik.values.durationMinutes}
            bookingIncrementMinutes={formik.values.bookingIncrementMinutes}
          />
          <div className="btn-wrap" style={{ display: "flex", marginTop: "2rem" }}>
            <Button variant="secondary" onClick={() => navigate("/app/manage-schedule-category")}>
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading} disabled={owners.isLoading || owners.isError || !owners.options.some(org => org.value === formik.values.organizationRef)}>
              Create
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AddCategory;
